import { apiFetch } from "@/lib/api";

export interface SimCardRow {
  created_at: string;
  iccid: string;
  carrier: string;
  activated: boolean;
  customer_owned: boolean;
  note: string | null;
}

export interface SimCsvValidRow {
  iccid: string;
  carrier: string;
  isNew: boolean;
}

export interface SimCsvErrorRow {
  iccid: string;
  carrier: string;
  rowIndex: number;
  errorType: string;
  errorMessage: string;
  existingCarrier?: string;
}

export interface SimCsvValidationResult {
  valid: SimCsvValidRow[];
  errors: SimCsvErrorRow[];
}

export interface SimCarrierApiStatus {
  verizon: boolean;
  vodafone: boolean;
  att: boolean;
}

export interface SimCarrierApiStatusResponse {
  carrierApis: SimCarrierApiStatus;
  verizonCallback: {
    configured: boolean;
    url: string | null;
  };
}

export const SIM_CARDS_QUERY_KEY = ["manufacturing", "sim-cards"] as const;

export interface SimCardsPageResult {
  sims: SimCardRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface FetchSimCardsParams {
  limit?: number;
  offset?: number;
  iccid?: string;
  carrier?: string;
  activated?: "" | "yes" | "no";
}

function simCardsQueryString(params: FetchSimCardsParams): string {
  const search = new URLSearchParams();
  if (params.limit != null) {
    search.set("limit", String(params.limit));
  }
  if (params.offset != null) {
    search.set("offset", String(params.offset));
  }
  if (params.iccid?.trim()) {
    search.set("iccid", params.iccid.trim());
  }
  if (params.carrier?.trim()) {
    search.set("carrier", params.carrier.trim());
  }
  if (params.activated === "yes") {
    search.set("activated", "true");
  } else if (params.activated === "no") {
    search.set("activated", "false");
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export async function fetchSimCards(params: FetchSimCardsParams = {}): Promise<SimCardsPageResult> {
  return apiFetch<SimCardsPageResult>(`/api/manufacturing/sim-cards${simCardsQueryString(params)}`);
}

export async function fetchSimLookup(iccid: string): Promise<SimCardRow | null> {
  const normalized = iccid.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  const data = await apiFetch<{ sim: SimCardRow | null }>(
    `/api/manufacturing/sim-cards/lookup?iccid=${encodeURIComponent(normalized)}`,
  );
  return data.sim;
}

export async function fetchSimCarriers(): Promise<string[]> {
  const data = await apiFetch<{ carriers: string[] }>("/api/manufacturing/sim-cards/carriers");
  return data.carriers;
}

export async function fetchSimCarrierApiStatus(): Promise<SimCarrierApiStatusResponse> {
  return apiFetch<SimCarrierApiStatusResponse>(
    "/api/manufacturing/sim-cards/carrier-api-status",
  );
}

export async function validateSimCsv(csvText: string): Promise<SimCsvValidationResult> {
  return apiFetch<SimCsvValidationResult>("/api/manufacturing/sim-cards/validate-csv", {
    method: "POST",
    body: { csvText },
  });
}

export async function bulkUpsertSimCards(input: {
  records: Array<{ iccid: string; carrier: string }>;
  activated: boolean;
  activateWithCarrier?: boolean;
  customerOwned?: boolean;
}): Promise<{ upserted: number }> {
  return apiFetch("/api/manufacturing/sim-cards/bulk-upsert", {
    method: "POST",
    body: input,
  });
}

export async function saveSimCard(input: {
  iccid: string;
  carrier: string;
  activated: boolean;
  customerOwned: boolean;
}): Promise<{ ok: boolean; iccid: string }> {
  return apiFetch("/api/manufacturing/sim-cards", {
    method: "POST",
    body: input,
  });
}

export async function activateSimWithCarrierApi(input: {
  iccid: string;
  carrier: string;
}): Promise<{ ok: boolean; activation: { carrier: string; message: string } }> {
  return apiFetch("/api/manufacturing/sim-cards/activate", {
    method: "POST",
    body: input,
  });
}

export interface EnsureSimActivatedResult {
  iccid: string;
  carrier: string;
  alreadyActivated: boolean;
  carrierActivationSubmitted: boolean;
  message: string;
}

export async function ensureSimActivated(input: {
  iccid: string;
  carrier?: string;
}): Promise<EnsureSimActivatedResult> {
  return apiFetch<EnsureSimActivatedResult>("/api/manufacturing/sim-cards/ensure-activated", {
    method: "POST",
    body: input,
  });
}
